import { useEffect, useState, useMemo } from 'react';
import Layout from '../components/Layout.jsx';
import { api } from '../api.js';
import { ProductoCard } from '../components/ProductoCard.jsx';

const fmt = (n) => '$ ' + new Intl.NumberFormat('es-CO').format(n ?? 0);

const CATS = [
  { v: 'todos', l: 'Todos', icon: '🌐' },
  { v: 'bebida_alcoholica', l: 'Alcohol', icon: '🍺' },
  { v: 'bebida_no_alcoholica', l: 'Bebidas', icon: '🥤' },
  { v: 'snack', l: 'Snacks', icon: '🍿' },
  { v: 'tabaqueria', l: 'Tabaco', icon: '🚬' },
  { v: 'farmacia', l: 'Farmacia', icon: '💊' },
  { v: 'otro', l: 'Otros', icon: '📦' },
];

export default function Vender() {
  const [turno, setTurno] = useState(null);
  const [productos, setProductos] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [carrito, setCarrito] = useState({});
  const [unidad, setUnidad] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [cat, setCat] = useState('todos');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const cargar = async () => {
    const [t, p, u] = await Promise.all([api.turnoAbierto(), api.productos(), api.unidades()]);
    setTurno(t.turno);
    setProductos(p.productos);
    setUnidades(u.unidades);
  };
  useEffect(() => { cargar(); }, []);

  const filtrados = useMemo(() => {
    return productos.filter((p) => {
      if (cat !== 'todos' && p.categoria !== cat) return false;
      if (busqueda && !p.nombre.toLowerCase().includes(busqueda.toLowerCase())) return false;
      return true;
    });
  }, [productos, cat, busqueda]);

  const lineas = Object.entries(carrito).map(([id, qty]) => {
    const p = productos.find((x) => x.id === Number(id));
    return p ? { ...p, cantidad: qty } : null;
  }).filter(Boolean);

  const total = lineas.reduce((s, l) => s + (l.precio_venta * l.cantidad), 0);
  const totalItems = lineas.reduce((s, l) => s + l.cantidad, 0);

  const add = (id) => setCarrito((c) => ({ ...c, [id]: (c[id] ?? 0) + 1 }));
  const sub = (id) => setCarrito((c) => {
    const n = (c[id] ?? 0) - 1;
    const nuevo = { ...c };
    if (n <= 0) delete nuevo[id];
    else nuevo[id] = n;
    return nuevo;
  });

  const limpiar = () => setCarrito({});

  const confirmar = async () => {
    if (lineas.length === 0) return;
    setLoading(true);
    setMsg('');
    try {
      for (const l of lineas) {
        await api.crearMovimiento({
          turno_id: turno?.id ?? null,
          unidad_id: unidad ? Number(unidad) : null,
          producto_id: l.id,
          cantidad: l.cantidad,
          tipo: 'venta',
          monto: l.precio_venta * l.cantidad,
          observacion: null,
        });
      }
      setMsg('✓ Venta registrada · ' + fmt(total));
      setCarrito({});
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow mb-2">Punto de venta</p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Vender productos</h1>
          {!turno && <p className="text-muted mt-3">⚠️ No hay turno abierto. Abre uno antes de vender.</p>}
        </div>
        {totalItems > 0 && (
          <div className="card-lime px-4 py-3 flex items-center gap-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">Carrito</div>
              <div className="font-bold text-xl tracking-tight">{fmt(total)}</div>
            </div>
            <span className="pill-ink">{totalItems} ud</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Catálogo */}
        <div className="lg:col-span-3">
          <div className="card mb-4 flex flex-wrap items-center gap-3">
            <input
              className="input flex-1 min-w-[200px]"
              placeholder="🔎 Buscar producto…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
            <div className="flex flex-wrap gap-2">
              {CATS.map((c) => (
                <button
                  key={c.v}
                  onClick={() => setCat(c.v)}
                  className={`px-3 py-2 rounded-2xl text-sm font-medium tracking-tight transition ${
                    cat === c.v ? 'bg-ink text-paper' : 'bg-bone text-ink hover:bg-line'
                  }`}
                >
                  <span className="mr-1">{c.icon}</span>{c.l}
                </button>
              ))}
            </div>
          </div>

          {filtrados.length === 0 ? (
            <div className="card text-center py-12 text-muted">
              <div className="text-4xl mb-3">🔎</div>
              <p>No hay productos en esta categoría o búsqueda.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filtrados.map((p) => (
                <ProductoCard
                  key={p.id}
                  producto={p}
                  cantidad={carrito[p.id] ?? 0}
                  onAdd={() => add(p.id)}
                  onSub={() => sub(p.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Carrito */}
        <div className="lg:col-span-1 space-y-4">
          <div className="card sticky top-20">
            <div className="flex items-center justify-between mb-3">
              <p className="eyebrow">Carrito</p>
              {lineas.length > 0 && (
                <button onClick={limpiar} className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted hover:text-ink">Limpiar</button>
              )}
            </div>

            {lineas.length === 0 ? (
              <p className="text-muted text-sm">Tu carrito está vacío. Toca + en cualquier producto.</p>
            ) : (
              <div className="space-y-2 max-h-[280px] overflow-y-auto mb-4">
                {lineas.map((l) => (
                  <div key={l.id} className="flex items-center justify-between gap-2 py-2 px-2 rounded-xl bg-bone/60">
                    <div className="min-w-0">
                      <div className="font-medium tracking-tight text-sm truncate">{l.nombre}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted mt-0.5">
                        {l.cantidad} × {fmt(l.precio_venta)}
                      </div>
                    </div>
                    <div className="font-mono text-sm font-semibold shrink-0">{fmt(l.precio_venta * l.cantidad)}</div>
                  </div>
                ))}
              </div>
            )}

            <div>
              <label className="label">Habitación (opcional)</label>
              <select className="input" value={unidad} onChange={(e) => setUnidad(e.target.value)}>
                <option value="">— sin asignar —</option>
                {unidades.filter((u) => u.tipo === 'habitacion').map((u) => (
                  <option key={u.id} value={u.id}>{u.codigo} · {u.nombre}</option>
                ))}
              </select>
            </div>

            <div className="my-4 pt-3 border-t border-line/70 flex items-center justify-between">
              <span className="eyebrow">Total</span>
              <span className="text-2xl font-bold tracking-tight font-mono">{fmt(total)}</span>
            </div>

            {msg && (
              <div className={`rounded-2xl px-3 py-2 text-xs font-mono mb-3 ${msg.startsWith('✓') ? 'bg-lime/30 text-ink' : 'bg-red-50 text-red-800 border border-red-200'}`}>{msg}</div>
            )}

            <button
              onClick={confirmar}
              className="btn-accent w-full"
              disabled={loading || lineas.length === 0 || !turno}
            >
              {loading ? 'Registrando…' : `Cobrar ${fmt(total)} →`}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
